import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  activeItemTo,
  activeSectionId,
  itemsOf,
  locate,
  menuTarget,
  type NavArea,
  type NavGroup,
  type NavItem,
  screensOf,
  sectionPath,
  sectionsOf,
} from '../navigation.ts';
import { Flyout } from './Flyout.tsx';
import { useShell } from './slots.tsx';

/** Recuo de cada nível da lateral: departamento, tela ou módulo do departamento, tela do módulo. */
const DEPTH = ['px-2.5', 'pr-2.5 pl-8', 'pr-2.5 pl-12'] as const;

/** Um item da lateral: uma tela ou, no menu enxuto, uma seção. */
export function MenuLink({
  to,
  label,
  icon: Icon,
  active,
  depth = 0,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  depth?: 0 | 1 | 2;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      activeOptions={{ exact: true }}
      aria-current={active ? 'page' : undefined}
      data-active={active ? 'true' : undefined}
      className={`flex min-h-9 items-center gap-2.5 rounded-md py-1.5 text-sm ${DEPTH[depth]} ${
        active ? 'ui-nav-active font-medium' : 'text-ink hover:bg-paper'
      }`}
    >
      <Icon
        size={depth === 0 ? 18 : 16}
        strokeWidth={1.75}
        aria-hidden
        className={`shrink-0 ${active ? '' : 'text-muted'}`}
      />
      {label}
    </Link>
  );
}

/** Botão que abre e fecha um departamento ou um módulo na lateral. */
export function Disclosure({
  label,
  icon: Icon,
  open,
  current,
  controls,
  depth,
  onToggle,
}: {
  label: string;
  icon?: LucideIcon;
  open: boolean;
  /** A tela atual está aqui dentro: o nome fica destacado mesmo fechado. */
  current: boolean;
  controls: string;
  depth: 0 | 1;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronDown : ChevronRight;
  const tone = current
    ? 'font-medium text-brand-text'
    : open
      ? 'font-medium text-ink'
      : depth === 1
        ? 'text-muted hover:text-ink'
        : 'text-ink';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className={`flex min-h-9 w-full items-center gap-2.5 rounded-md py-1.5 text-left text-sm hover:bg-paper ${DEPTH[depth]} ${tone}`}
    >
      {Icon && (
        <Icon
          size={18}
          strokeWidth={1.75}
          aria-hidden
          className={`shrink-0 ${current ? 'text-brand-text' : 'text-muted'}`}
        />
      )}
      <span className="flex-1">{label}</span>
      <Chevron size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
    </button>
  );
}

/**
 * As telas de um departamento aberto. Menu enxuto (Dados Mestres): a visão geral e uma entrada por
 * seção. Nos outros: as telas soltas e um módulo (grupo com título) de cada vez; departamento com um
 * grupo só mostra as telas direto.
 */
export function AreaTree({
  area,
  idPrefix,
  openGroup,
  onToggleGroup,
  onNavigate,
}: {
  area: NavArea;
  idPrefix: string;
  openGroup: string | undefined;
  onToggleGroup: (key: string) => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Só um item marcado: o de endereço mais longo (ver activeItemTo).
  const activeTo = activeItemTo([area], pathname);
  const link = (item: NavItem, depth: 1 | 2) => (
    <MenuLink
      key={item.to}
      to={item.to}
      label={t(item.key)}
      icon={item.icon}
      active={item.to === activeTo}
      depth={depth}
      onNavigate={onNavigate}
    />
  );
  if (area.sections) {
    const activeSection = activeSectionId(area, pathname);
    return (
      <>
        {area.groups
          .filter((g) => !g.section)
          .flatMap((g) => g.items)
          .map((item) => link(item, 1))}
        {sectionsOf(area).map((section) => (
          <MenuLink
            key={section.id}
            to={sectionPath(area, section)}
            label={t(section.key)}
            icon={section.icon}
            active={section.id === activeSection}
            depth={1}
            onNavigate={onNavigate}
          />
        ))}
      </>
    );
  }
  if (area.groups.length === 1) return <>{area.groups[0]!.items.map((i) => link(i, 1))}</>;
  const currentGroup = locate([area], pathname).group;
  const module = (group: NavGroup, gi: number) => {
    const id = `${idPrefix}-${area.id}-group-${gi}`;
    const open = openGroup === group.key;
    return (
      <div key={group.key} className="flex flex-col gap-0.5">
        <Disclosure
          label={t(group.key!)}
          open={open}
          current={group === currentGroup && activeTo !== undefined}
          controls={id}
          depth={1}
          onToggle={() => onToggleGroup(group.key!)}
        />
        {open && (
          <div id={id} className="flex flex-col gap-0.5">
            {group.items.map((i) => link(i, 2))}
          </div>
        )}
      </div>
    );
  };
  return (
    <>
      {area.groups.map((group, gi) =>
        group.key ? module(group, gi) : group.items.map((i) => link(i, 1)),
      )}
    </>
  );
}

/**
 * Lista da lateral única (ADR 0071): Início e os departamentos. Configuração fica no rodapé
 * (FooterLinks). Abre sozinha o departamento e o módulo da tela atual; um departamento aberto por vez.
 */
export function SidebarNav({
  areas,
  idPrefix,
  initialArea,
  onNavigate,
}: {
  areas: NavArea[];
  idPrefix: string;
  /** Departamento escolhido na faixa recolhida: abre no lugar do da tela atual. */
  initialArea?: string;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const target = menuTarget(areas, pathname);
  const [openArea, setOpenArea] = useState(initialArea ?? target.areaId);
  const [openGroup, setOpenGroup] = useState(
    initialArea && initialArea !== target.areaId ? undefined : target.groupKey,
  );
  // Mudou de tela: abre o departamento e o módulo dela (ajuste durante a renderização, sem efeito).
  const [syncedPath, setSyncedPath] = useState(pathname);
  if (syncedPath !== pathname) {
    setSyncedPath(pathname);
    if (target.areaId) setOpenArea(target.areaId);
    setOpenGroup(target.groupKey);
  }
  const currentArea = locate(areas, pathname).area;
  const homeActive = activeItemTo(areas, pathname);
  const submenu = useShell()?.options.submenu ?? 'accordion';
  // A tela atual fica à vista na lateral (link direto para uma tela do fim da lista, por exemplo).
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [pathname]);

  const toggleArea = (area: NavArea) => {
    const opening = openArea !== area.id;
    setOpenArea(opening ? area.id : undefined);
    setOpenGroup(opening && area.id === target.areaId ? target.groupKey : undefined);
  };

  const entry = (area: NavArea) => {
    if (screensOf(area).length === 0) {
      // Em preparação: só a página inicial, sem nada para abrir.
      const home = itemsOf(area)[0]!;
      const active = homeActive === home.to;
      const Icon = area.icon;
      return (
        <Link
          key={area.id}
          to={home.to}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={`flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm ${
            active ? 'ui-nav-active font-medium' : 'text-ink hover:bg-paper'
          }`}
        >
          <Icon
            size={18}
            strokeWidth={1.75}
            aria-hidden
            className={`shrink-0 ${active ? '' : 'text-muted'}`}
          />
          <span className="flex-1">{t(area.key)}</span>
          <span className="shrink-0 rounded-full border border-rule px-1.5 text-[11px] leading-4 text-muted">
            {t('nav.preparing')}
          </span>
        </Link>
      );
    }
    const id = `${idPrefix}-area-${area.id}`;
    // Submenu (ajuste de layout `submenu`, ADR 0133): abre embaixo (um por vez), sempre aberto, ou
    // flutua ao lado ao passar o mouse ou com o foco do teclado.
    if (submenu === 'flyout') {
      return (
        <Flyout key={area.id} area={area} current={area.id === currentArea?.id}>
          <AreaTree
            area={area}
            idPrefix={idPrefix}
            openGroup={openGroup}
            onToggleGroup={(key) => setOpenGroup((g) => (g === key ? undefined : key))}
            onNavigate={onNavigate}
          />
        </Flyout>
      );
    }
    const open = submenu === 'inline' || openArea === area.id;
    return (
      <div key={area.id} className="flex flex-col gap-0.5">
        <Disclosure
          label={t(area.key)}
          icon={area.icon}
          open={open}
          current={area.id === currentArea?.id}
          controls={id}
          depth={0}
          onToggle={() => toggleArea(area)}
        />
        {open && (
          <div id={id} className="flex flex-col gap-0.5">
            <AreaTree
              area={area}
              idPrefix={idPrefix}
              openGroup={openGroup}
              onToggleGroup={(key) => setOpenGroup((g) => (g === key ? undefined : key))}
              onNavigate={onNavigate}
            />
          </div>
        )}
      </div>
    );
  };

  const home = areas.find((a) => a.id === 'home');
  const departments = areas.filter((a) => a.id !== 'home' && !a.bottom);
  return (
    <nav ref={navRef} aria-label={t('nav.label')} className="flex flex-col gap-0.5">
      {home &&
        itemsOf(home).map((item) => (
          <MenuLink
            key={item.to}
            to={item.to}
            label={t(item.key)}
            icon={item.icon}
            active={item.to === homeActive}
            onNavigate={onNavigate}
          />
        ))}
      {departments.length > 0 && <div className="mt-3" />}
      {departments.map(entry)}
    </nav>
  );
}

/** Atalho só com ícone no rodapé da lateral (Configuração e Ajuda). */
export function FooterIcon({
  to,
  label,
  icon: Icon,
  active,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-label={label}
      title={label}
      aria-current={active ? 'page' : undefined}
      className={`flex size-9 shrink-0 items-center justify-center rounded-md ${
        active ? 'ui-nav-active' : 'text-muted hover:bg-paper hover:text-ink'
      }`}
    >
      <Icon size={18} strokeWidth={1.75} aria-hidden />
    </Link>
  );
}

/** A Configuração (rodapé da lateral) e a primeira tela dela, se a pessoa pode ver alguma. */
export function settingsEntry(areas: NavArea[]) {
  const area = areas.find((a) => a.id === 'settings');
  return { settingsArea: area, settings: area ? itemsOf(area)[0] : undefined };
}
